<script setup>
// Trail directory: every public trail, grouped by network, with the
// network's own description beside its trails. The Trails page starts
// with it; it can sit on any page.
import { computed, inject, reactive, ref } from 'vue';
import DifficultyIcon from '@/Components/DifficultyIcon.vue';
import { DIFFICULTY_KEYS, difficultyLabel } from '@/lib/trailLabels.js';
import './trailsPage.css';
import {
    blockStyle, difficultyIconOf, distanceText, groupStatsLine, networkHref, shortDescription, statusOf, trailGroupsOf,
    trailHref, trailMetaLine,
} from '../trailsPage.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const query = ref('');
const network = ref('all');
const difficulty = ref('all');
const expanded = reactive({});

const picked = computed(() => (props.config.network_ids || []).map(Number));
// No trails is no block for a visitor, heading included; the editor
// keeps its line (Mus, 2026-10-11). A search that matches nothing is
// an answer to the visitor and still says so.
const editing = inject('tiqEditorCanvas', false);
const groups = computed(() => {
    const all = trailGroupsOf(props.site_data).filter((g) => (g.trails || []).length);
    if (!picked.value.length) return all;
    return all.filter((g) => g.network_id !== null && picked.value.includes(Number(g.network_id)));
});
const groupKey = (g) => String(g.network_id ?? 'none');

const difficulties = computed(() => DIFFICULTY_KEYS.filter((k) => groups.value.some((g) => g.trails.some((t) => t.difficulty === k))));

const perNetwork = computed(() => {
    const n = Number(props.config.per_network);
    return Number.isFinite(n) && n > 0 ? n : 0;
});

const shown = computed(() => {
    const q = query.value.trim().toLowerCase();
    return groups.value
        .filter((g) => network.value === 'all' || groupKey(g) === network.value)
        .map((g) => {
            const trails = g.trails.filter((t) => (difficulty.value === 'all' || t.difficulty === difficulty.value)
                && (!q || String(t.name || '').toLowerCase().includes(q)));
            const open = expanded[groupKey(g)] || !perNetwork.value;
            return { group: g, trails, visible: open ? trails : trails.slice(0, perNetwork.value) };
        })
        .filter((x) => x.trails.length);
});

const filtering = computed(() => !!query.value.trim() || network.value !== 'all' || difficulty.value !== 'all');
const showDescriptions = computed(() => props.config.show_descriptions !== false);
const accent = computed(() => props.config.style?.accent || props.accent || 'var(--th-accent, #2f6b47)');
</script>

<template>
    <section v-if="groups.length || editing" class="tp tdir" :id="config.anchor_id || null" :style="[blockStyle(config), { '--tp-accent': accent }]">
        <div class="tp-wrap">
            <header v-if="config.title || config.subtitle" class="tp-head">
                <h2 v-if="config.title" class="tp-title">{{ config.title }}</h2>
                <p v-if="config.subtitle" class="tp-sub">{{ config.subtitle }}</p>
            </header>

            <div class="tdir-filters">
                <input
                    v-if="config.show_search !== false"
                    v-model="query"
                    type="search"
                    class="tdir-search"
                    :placeholder="config.search_placeholder || 'Search trails by name'"
                    aria-label="Search trails by name"
                />
                <div v-if="config.show_network_filter !== false && groups.length > 1" class="tdir-chips" role="group" aria-label="Network">
                    <button type="button" class="tp-chip" :class="{ 'is-on': network === 'all' }" :aria-pressed="network === 'all'" @click="network = 'all'">All networks</button>
                    <button
                        v-for="g in groups"
                        :key="groupKey(g)"
                        type="button"
                        class="tp-chip"
                        :class="{ 'is-on': network === groupKey(g) }"
                        :aria-pressed="network === groupKey(g)"
                        @click="network = groupKey(g)"
                    >{{ g.network_name }}</button>
                </div>
                <div v-if="config.show_difficulty_filter !== false && difficulties.length > 1" class="tdir-chips is-small" role="group" aria-label="Difficulty">
                    <button type="button" class="tp-chip" :class="{ 'is-on': difficulty === 'all' }" :aria-pressed="difficulty === 'all'" @click="difficulty = 'all'">Any difficulty</button>
                    <button
                        v-for="d in difficulties"
                        :key="d"
                        type="button"
                        class="tp-chip"
                        :class="{ 'is-on': difficulty === d }"
                        :aria-pressed="difficulty === d"
                        @click="difficulty = d"
                    >{{ difficultyLabel(d) }}</button>
                </div>
            </div>

            <div v-for="x in shown" :key="groupKey(x.group)" class="tdir-net">
                <div class="tdir-about">
                    <h3 class="tdir-name">
                        <a v-if="networkHref(x.group)" :href="networkHref(x.group)" class="tdir-namelink">{{ x.group.network_name }}</a>
                        <template v-else>{{ x.group.network_name }}</template>
                    </h3>
                    <p v-if="x.group.network_location" class="tp-mono tdir-loc">{{ x.group.network_location }}</p>
                    <!-- A long description is cut and the rest is on the
                         network's own page; "Other trails" has no page,
                         so it always shows whole. -->
                    <p v-if="showDescriptions && x.group.network_description" class="tdir-desc">
                        <template v-if="networkHref(x.group) && shortDescription(x.group.network_description)">{{ shortDescription(x.group.network_description) }}
                            <a :href="networkHref(x.group)" class="tdir-readmore">Read more</a></template>
                        <template v-else>{{ x.group.network_description }}</template>
                    </p>
                    <p class="tp-mono tdir-stats">{{ groupStatsLine(x.group, site_data) }}</p>
                </div>
                <div class="tdir-list">
                    <a
                        v-for="t in x.visible"
                        :key="t.id"
                        :href="trailHref(t) || undefined"
                        class="tdir-row"
                    >
                        <span class="tdir-main">
                            <span class="tdir-trail">
                                <DifficultyIcon
                                    v-if="difficultyIconOf(t, org)"
                                    class="tdir-icon"
                                    :difficulty="difficultyIconOf(t, org).difficulty"
                                    :icon-set="difficultyIconOf(t, org).set"
                                    :color="difficultyIconOf(t, org).color"
                                />
                                {{ t.name }}
                            </span>
                            <span v-if="trailMetaLine(t)" class="tdir-meta">{{ trailMetaLine(t) }}</span>
                        </span>
                        <span v-if="distanceText(t)" class="tdir-dist">{{ distanceText(t) }}</span>
                        <span v-if="statusOf(t, site_data)" class="tp-pill" :class="`is-${statusOf(t, site_data).tone}`">{{ statusOf(t, site_data).label }}</span>
                        <span v-if="trailHref(t)" class="tdir-go" aria-hidden="true">→</span>
                    </a>
                    <button
                        v-if="x.visible.length < x.trails.length"
                        type="button"
                        class="tdir-more"
                        @click="expanded[groupKey(x.group)] = true"
                    >Show all {{ x.trails.length }} trails</button>
                </div>
            </div>

            <p v-if="!shown.length" class="tp-empty">
                {{ filtering ? 'No trails match those filters.' : 'No trails to show yet.' }}
            </p>
        </div>
    </section>
</template>

<style scoped>
.tdir-filters { display: grid; gap: 12px; margin-bottom: 32px; }
.tdir-search {
    width: 100%; max-width: 420px; box-sizing: border-box;
    font: inherit; font-size: 16px; padding: 13px 14px;
    border: 1px solid var(--tp-line); border-radius: 4px; background: var(--tp-card); color: var(--tp-ink);
}
.tdir-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.tdir-chips.is-small .tp-chip { font-size: 13px; padding: 5px 12px; }
.tdir-net {
    display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.9fr); gap: 20px 48px;
    border-top: 2px solid var(--tp-ink); padding: 20px 0 44px;
}
.tdir-name {
    font-family: var(--tp-display); font-weight: 800; font-size: clamp(26px, 3vw, 34px);
    letter-spacing: -0.01em; line-height: 1.1; margin: 0; color: var(--text-heading, inherit);
}
.tdir-loc { color: var(--tp-meta, var(--tp-faint)); margin: 8px 0 0; }
.tdir-desc { color: var(--tp-muted); line-height: 1.55; margin: 10px 0 0; white-space: pre-line; }
.tdir-namelink { color: inherit; text-decoration: none; }
.tdir-namelink:hover { text-decoration: underline; text-underline-offset: 3px; }
.tdir-readmore { white-space: nowrap; font-weight: 600; color: var(--text-heading, inherit); text-decoration: none; border-bottom: 2px solid var(--tp-accent); }
.tdir-readmore:hover { text-decoration: none; border-bottom-color: currentColor; }
.tdir-stats { color: var(--tp-accent); font-weight: 700; margin: 12px 0 0; }
.tdir-list { border: 1px solid var(--tp-line); border-radius: 4px; background: var(--tp-card); align-self: start; overflow: hidden; }
.tdir-row {
    display: flex; align-items: center; gap: 18px; padding: 18px 20px;
    text-decoration: none; color: inherit; border-top: 1px solid var(--tp-line);
}
.tdir-row:first-child { border-top: 0; }
.tdir-row:hover { text-decoration: none; }
.tdir-row[href]:hover { background: var(--th-line-soft, #f5f4ef); }
.tdir-main { flex: 1; min-width: 0; display: grid; gap: 4px; }
.tdir-trail { display: flex; align-items: center; gap: 8px; font-weight: 700; font-size: 18px; }
.tdir-icon { width: 16px; height: 16px; flex: none; }
.tdir-meta { color: var(--tp-muted); font-size: 15px; }
.tdir-dist { font-family: var(--tp-mono); font-size: 15px; white-space: nowrap; }
.tdir-go { color: var(--tp-ink); }
.tdir-more {
    display: block; width: 100%; font: inherit; font-weight: 700; font-size: 14px;
    padding: 14px 20px; text-align: left; background: none; cursor: pointer;
    border: 0; border-top: 1px solid var(--tp-line); color: var(--tp-accent);
}
@media (max-width: 760px) {
    .tdir-net { grid-template-columns: 1fr; padding-bottom: 32px; }
    .tdir-row { gap: 10px; padding: 14px 16px; flex-wrap: wrap; }
    .tdir-main { flex-basis: 100%; }
    .tdir-row > .tp-pill { margin-left: auto; }
    .tdir-row > .tdir-dist + .tdir-go { margin-left: auto; }
}
</style>
